import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { getStatus, setup, enable, disable } from '../../services/TwoFaService';
import logger from '../../utils/logger';

/**
 * Self-contained TOTP two-factor card: enroll (QR + confirm) / disable.
 */
function TwoFactorCard() {
    const { formatMessage } = useIntl();
    const t = (id) => formatMessage({ id });

    const [enabled, setEnabled] = useState(false);
    const [enrolling, setEnrolling] = useState(null); // { qrDataUrl, secret }
    const [code, setCode] = useState('');
    const [msg, setMsg] = useState(null); // { type, text }

    useEffect(() => {
        getStatus().then(s => setEnabled(!!s.enabled)).catch(err => logger.error(err.message));
    }, []);

    function startEnroll() {
        setMsg(null);
        setup().then(data => setEnrolling(data)).catch(err => setMsg({ type: 'danger', text: err.response ? err.response.data : err.message }));
    }

    function confirmEnroll() {
        enable(code).then(() => { setEnabled(true); setEnrolling(null); setCode(''); setMsg({ type: 'success', text: t('twofa.enabled') }); })
            .catch(err => setMsg({ type: 'danger', text: err.response ? err.response.data : err.message }));
    }

    function turnOff() {
        disable(code).then(() => { setEnabled(false); setCode(''); setMsg({ type: 'success', text: t('twofa.disabled') }); })
            .catch(err => setMsg({ type: 'danger', text: err.response ? err.response.data : err.message }));
    }

    return (
        <div className="card card-body border-0 shadow mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h2 className="h5 mb-0">{t('twofa.title')}</h2>
                {enabled
                    ? <span className="badge bg-success">{t('twofa.on')}</span>
                    : <span className="badge bg-secondary">{t('twofa.off')}</span>}
            </div>
            <p className="small text-muted">{t('twofa.desc')}</p>

            {msg && <div className={`alert alert-${msg.type} py-2`}>{msg.text}</div>}

            {!enabled && !enrolling && (
                <div className="d-grid d-md-block">
                    <button className="btn btn-gray-800" type="button" onClick={startEnroll}>{t('twofa.enable')}</button>
                </div>
            )}

            {!enabled && enrolling && (
                <div>
                    <p className="small mb-2">{t('twofa.scan')}</p>
                    <div className="text-center mb-2">
                        <img src={enrolling.qrDataUrl} alt="2FA QR" style={{ width: 180, height: 180 }} />
                    </div>
                    <p className="small text-muted">{t('twofa.manual')} <code className="text-break-all">{enrolling.secret}</code></p>
                    <div className="form-group mb-2">
                        <label htmlFor="twofaCode">{t('twofa.codeLabel')}</label>
                        <input className="form-control" id="twofaCode" type="text" inputMode="numeric" maxLength={6} placeholder="123456" value={code} onChange={e => setCode(e.target.value)} />
                    </div>
                    <button className="btn btn-success me-2" type="button" onClick={confirmEnroll}>{t('twofa.confirm')}</button>
                    <button className="btn btn-link" type="button" onClick={() => { setEnrolling(null); setCode(''); }}>{t('common.cancel')}</button>
                </div>
            )}

            {enabled && (
                <div>
                    <div className="form-group mb-2" style={{ maxWidth: 240 }}>
                        <label htmlFor="twofaCodeOff">{t('twofa.codeLabel')}</label>
                        <input className="form-control" id="twofaCodeOff" type="text" inputMode="numeric" maxLength={6} placeholder="123456" value={code} onChange={e => setCode(e.target.value)} />
                    </div>
                    <button className="btn btn-outline-danger" type="button" onClick={turnOff}>{t('twofa.disable')}</button>
                </div>
            )}
        </div>
    );
}

export default TwoFactorCard;
